import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  X,
  Check,
  Globe,
  Plane,
  Wallet,
  Award,
  Wrench,
  ShieldCheck,
  Users,
  ArrowDown,
  Zap,
  Clock,
} from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// TODO: Replace with your n8n webhook URL once configured
const N8N_WEBHOOK_URL = 'https://casunlimited.app.n8n.cloud/webhook/careers-form';

const tradeOptions = [
  'PLC Programmer',
  'Robot Programmer',
  'Electrician',
  'Mechanical Specialist',
  'Systems Integrator',
  'Commissioning Engineer',
  'Other',
];

const experienceOptions = ['0-2', '3-5', '6-10', '10+'];
const redSealOptions = ['Yes — Certified', 'Yes — In Progress', 'No'];
const passportOptions = ['Valid Passport', 'Expired / No Passport', 'In Application'];
const availabilityOptions = ['Immediate', '2 Weeks', '1 Month', '3+ Months'];
const visaOptions = ['US B1B2', 'No Visa'];

const countryOptions = [
  'South Africa',
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia',
  'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
  'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Croatia',
  'Cuba', 'Cyprus', 'Czechia', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine',
  'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States of America', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

const trustPoints = [
  { icon: Wallet, label: 'Competitive USD Rates' },
  { icon: Clock, label: 'Paid On Time' },
  { icon: Globe, label: 'Global Opportunities' },
  { icon: Plane, label: 'Professional Deployment' },
  { icon: Users, label: 'Long-Term Relationships' },
  { icon: ShieldCheck, label: 'Trusted Industrial Partner' },
];

const benefitCards = [
  {
    icon: Globe,
    title: 'International Projects',
    desc: 'Work in advanced manufacturing facilities across North America.',
  },
  {
    icon: Plane,
    title: 'Professional Deployment',
    desc: 'From onboarding to travel arrangements, our deployment team manages every step.',
  },
  {
    icon: Wallet,
    title: 'Reliable Payments',
    desc: 'Competitive USD rates with dependable, on-time payments.',
  },
  {
    icon: Users,
    title: 'Long-Term Relationships',
    desc: 'We invest in long-term partnerships with exceptional specialists.',
  },
  {
    icon: Award,
    title: 'Career Growth',
    desc: 'Gain experience with advanced automation technologies used by leading manufacturers.',
  },
  {
    icon: Wrench,
    title: 'Industry Respect',
    desc: 'Join a network of professionals recognised for quality workmanship and technical excellence.',
  },
];

const galleryImages = [
  { src: '/contractor-hero.webp', alt: 'STRATON industrial deployment' },
  { src: '/about-deployment.webp', alt: 'On-site deployment team' },
  { src: '/about-automation.webp', alt: 'Advanced automation systems' },
  { src: '/solution-deployment.webp', alt: 'Industrial deployment preparation' },
  { src: '/solution-automation.webp', alt: 'Automation integration project' },
  { src: '/project-hero.webp', alt: 'STRATON project environment' },
];

const opportunities = [
  'Electrical Specialists',
  'Mechanical Specialists',
  'PLC Programmers',
  'Robot Programmers',
  'Commissioning Engineers',
  'Industrial Electricians',
  'Automation Technicians',
];

const processSteps = [
  'Submit your application',
  'Our deployment team reviews your profile',
  'Suitable candidates are contacted',
  'Technical screening',
  'Deployment preparation',
  'International placement',
];

interface FileUploadProps {
  label: string;
  required?: boolean;
  onFileSelect: (file: File | null) => void;
  file: File | null;
  acceptedTypes?: string;
}

function FileUpload({ label, required, onFileSelect, file, acceptedTypes = '.pdf,.doc,.docx' }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) onFileSelect(droppedFile);
  }, [onFileSelect]);

  const handleClick = () => inputRef.current?.click();

  return (
    <div className="w-full">
      <label className="label-small block mb-3">
        {label} {required && <span className="text-text-secondary">*</span>}
      </label>
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`cursor-pointer border-2 border-dashed p-8 md:p-10 text-center transition-colors ${
          isDragging
            ? 'border-text-primary bg-bg-tertiary'
            : 'border-border-primary bg-bg-primary hover:border-text-muted hover:bg-bg-tertiary'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes}
          onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
          className="hidden"
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <Check size={20} className="text-text-primary" />
            <span className="body-text text-text-primary">{file.name}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFileSelect(null);
              }}
              className="p-1 hover:bg-bg-tertiary transition-colors"
            >
              <X size={16} className="text-text-muted" />
            </button>
          </div>
        ) : (
          <>
            <Upload size={28} className="mx-auto mb-3 text-text-muted" />
            <p className="body-text mb-1">Tap to upload or drop your file here</p>
            <p className="body-sm">PDF, DOC, DOCX — Max 10MB</p>
          </>
        )}
      </div>
    </div>
  );
}

function scrollToForm() {
  const el = document.getElementById('application-form');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

export default function Careers() {
  useDocumentTitle('Careers — Industrial Automation Jobs | Straton Group');

  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Join STRATON Group — global industrial automation jobs for PLC programmers, robot programmers, electrical & mechanical specialists. Competitive USD rates, professional deployment, on-time payments.'
      );
    }
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'Industrial Automation Jobs, Electrical Specialist Jobs USA, Mechanical Specialist Jobs USA, PLC Programmer Jobs, Robot Programmer Careers, Industrial Field Service Jobs, Automation Technician Jobs, Manufacturing Careers, Commissioning Engineer Jobs'
      );
    }
  }, []);

  const [cvFile, setCvFile] = useState<File | null>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    location: '',
    trade: '',
    trade_other: '',
    experience: '',
    red_seal: '',
    passport: '',
    visa: '',
    availability: '',
    motivation: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (cvFile) data.append('cv', cvFile);
      if (docFile) data.append('documents', docFile);

      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        body: data,
      });

      if (!response.ok) throw new Error('Submission failed');
      setSubmitted(true);
      window.scrollTo({ top: document.getElementById('application-form')?.offsetTop || 0, behavior: 'smooth' });
    } catch {
      setError('Something went wrong. Please try again or contact us directly at info@stratongroup.co.za');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content">
      {/* Hero */}
      <section className="min-h-screen flex flex-col lg:flex-row bg-bg-primary relative">
        <div className="lg:w-[45%] flex flex-col justify-center section-padding pt-[100px] lg:pt-[72px] pb-16 lg:pb-0 relative z-10">
          <div className="max-w-lg">
            <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted mb-6 block">
              International Industrial Careers
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary leading-tight mb-8">
              Global Automation Specialists Wanted
            </h1>
            <div className="w-12 h-px bg-text-muted mb-8" />
            <p className="body-lg mb-6">
              Join an international network of highly skilled industrial automation specialists working on advanced manufacturing projects across North America and beyond.
            </p>
            <p className="body-text mb-10 text-text-secondary">
              Whether you're an Electrical Specialist, Mechanical Specialist, PLC Programmer or Robot Programmer, STRATON connects experienced professionals with world-class opportunities.
            </p>

            <button
              onClick={scrollToForm}
              className="btn-primary py-4 px-8 text-xs font-bold uppercase tracking-[0.15em] mb-12"
            >
              Join the STRATON Network
            </button>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-5 gap-x-4">
              {trustPoints.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.12em] text-text-secondary leading-tight">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:w-[55%] relative min-h-[50vh] lg:min-h-screen overflow-hidden">
          <img
            src="/contractor-hero.webp"
            fetchPriority="high"
            alt="STRATON industrial facility"
            className="w-full h-full object-cover grayscale brightness-[0.7]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/40 to-transparent" />
          <button
            onClick={scrollToForm}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 text-text-muted hover:text-text-primary transition-colors animate-bounce"
            aria-label="Scroll to application form"
          >
            <ArrowDown size={24} strokeWidth={1.5} />
          </button>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-24 md:py-32 bg-bg-secondary section-padding">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl mb-16">
            <span className="label-small block mb-4">Why Join</span>
            <h2 className="heading-lg text-text-primary mb-6">Why Contractors Choose STRATON</h2>
            <p className="body-text text-text-secondary">
              We build long-term partnerships with specialists who take pride in their craft. Here's what you can expect when you join the network.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-secondary">
            {benefitCards.map((card) => (
              <div key={card.title} className="bg-bg-primary p-8 md:p-10">
                <card.icon className="w-6 h-6 text-text-muted mb-5" strokeWidth={1.5} />
                <h3 className="heading-sm text-text-primary mb-3">{card.title}</h3>
                <p className="body-sm text-text-secondary">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-24 md:py-32 bg-bg-primary section-padding">
        <div className="max-w-[900px] mx-auto text-center">
          <span className="label-small block mb-4">Our Promise</span>
          <h2 className="heading-lg text-text-primary mb-8">We Believe Skilled Professionals Deserve Professional Treatment</h2>
          <div className="divider mb-10" />
          <div className="space-y-4 body-lg text-text-secondary">
            <p>We communicate clearly.</p>
            <p>We honour our commitments.</p>
            <p>We pay on time.</p>
            <p>Every deployment is built on trust.</p>
          </div>
        </div>
      </section>

      {/* Real World Experience */}
      <section className="py-24 md:py-32 bg-bg-secondary section-padding">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="label-small block mb-4">Real World Experience</span>
            <h2 className="heading-lg text-text-primary mb-4">Life On Deployment</h2>
            <p className="body-text text-text-secondary">
              Genuine industrial environments, real teams and meaningful projects. This is what deployment looks like with STRATON.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="aspect-[4/3] overflow-hidden bg-bg-primary">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Opportunities */}
      <section className="py-24 md:py-32 bg-bg-primary section-padding">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="label-small block mb-4">Opportunities</span>
            <h2 className="heading-lg text-text-primary mb-4">Current Opportunities</h2>
            <p className="body-text text-text-secondary">
              We are always looking for experienced specialists to join our network. If your trade matches one of the roles below, we encourage you to apply.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {opportunities.map((role) => (
              <div
                key={role}
                className="bg-bg-secondary border border-border-secondary p-6 flex items-center gap-3 hover:border-text-muted transition-colors"
              >
                <Zap className="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium text-text-primary">{role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-24 md:py-32 bg-bg-secondary section-padding">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="label-small block mb-4">The Process</span>
            <h2 className="heading-lg text-text-primary mb-4">What Happens Next?</h2>
            <p className="body-text text-text-secondary">
              Our recruitment process is straightforward and transparent. Here is exactly what to expect after you submit your application.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-4 bg-bg-primary p-6 border border-border-secondary">
                <span className="text-2xl font-bold text-text-muted">{String(idx + 1).padStart(2, '0')}</span>
                <p className="body-text text-text-primary pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-24 md:py-32 bg-bg-primary section-padding scroll-mt-20">
        <div className="max-w-[900px] mx-auto">
          <span className="label-small block mb-4">Apply Now</span>
          <h2 className="heading-lg text-text-primary mb-4">Join the STRATON Network</h2>
          <p className="body-text mb-10 text-text-secondary">
            Complete the form below and our deployment team will review your application. If your skills match current or upcoming opportunities, we'll contact you directly.
          </p>
          <div className="divider mb-12" />

          {submitted ? (
            <div className="bg-bg-secondary border border-border-secondary p-12 text-center">
              <Check size={48} className="mx-auto mb-6 text-text-primary" />
              <h3 className="heading-md text-text-primary mb-4">Thank You</h3>
              <div className="space-y-3 body-text text-text-secondary max-w-lg mx-auto">
                <p>Your application has been received successfully.</p>
                <p>Our deployment team will review your information.</p>
                <p>
                  If your skills match current or upcoming opportunities, we'll contact you regarding the next steps. Even if there isn't an immediate opening, we'll keep your profile in our specialist database for future deployments.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="border border-red-900/50 bg-red-950/20 p-4 text-xs text-red-300">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="career-first-name" className="label-small block mb-3">First Name <span className="text-text-secondary">*</span></label>
                  <input id="career-first-name" name="first_name" type="text" required value={form.first_name} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors" placeholder="Enter first name" />
                </div>
                <div>
                  <label htmlFor="career-last-name" className="label-small block mb-3">Last Name / Surname <span className="text-text-secondary">*</span></label>
                  <input id="career-last-name" name="last_name" type="text" required value={form.last_name} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors" placeholder="Enter last name" />
                </div>
                <div>
                  <label htmlFor="career-email" className="label-small block mb-3">Email Address <span className="text-text-secondary">*</span></label>
                  <input id="career-email" name="email" type="email" required value={form.email} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors" placeholder="Enter email address" />
                </div>
                <div>
                  <label htmlFor="career-phone" className="label-small block mb-3">Phone Number <span className="text-text-secondary">*</span></label>
                  <input id="career-phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors" placeholder="Enter phone number" />
                </div>
                <div>
                  <label htmlFor="career-location" className="label-small block mb-3">Location / Country <span className="text-text-secondary">*</span></label>
                  <select id="career-location" name="location" required value={form.location} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select country</option>
                    {countryOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="career-trade" className="label-small block mb-3">Trade / Specialization <span className="text-text-secondary">*</span></label>
                  <select id="career-trade" name="trade" required value={form.trade} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select trade</option>
                    {tradeOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                {form.trade === 'Other' && (
                  <div className="md:col-span-2">
                    <label htmlFor="career-trade-other" className="label-small block mb-3">Please specify your trade / specialization <span className="text-text-secondary">*</span></label>
                    <input id="career-trade-other" name="trade_other" type="text" required value={form.trade_other} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors" placeholder="Enter your trade or specialization" />
                  </div>
                )}
                <div>
                  <label htmlFor="career-experience" className="label-small block mb-3">Years of Experience <span className="text-text-secondary">*</span></label>
                  <select id="career-experience" name="experience" required value={form.experience} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select experience</option>
                    {experienceOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="career-redseal" className="label-small block mb-3">Red Seal Status <span className="text-text-secondary">*</span></label>
                  <select id="career-redseal" name="red_seal" required value={form.red_seal} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select status</option>
                    {redSealOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="career-passport" className="label-small block mb-3">Passport Status <span className="text-text-secondary">*</span></label>
                  <select id="career-passport" name="passport" required value={form.passport} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select status</option>
                    {passportOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="career-visa" className="label-small block mb-3">Visa Status <span className="text-text-secondary">*</span></label>
                  <select id="career-visa" name="visa" required value={form.visa} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select visa</option>
                    {visaOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="career-availability" className="label-small block mb-3">Availability <span className="text-text-secondary">*</span></label>
                  <select id="career-availability" name="availability" required value={form.availability} onChange={handleChange} className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors appearance-none">
                    <option value="">Select availability</option>
                    {availabilityOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="career-motivation" className="label-small block mb-3">Additional Information</label>
                <textarea
                  id="career-motivation"
                  name="motivation"
                  rows={5}
                  maxLength={3000}
                  value={form.motivation}
                  onChange={handleChange}
                  className="w-full bg-bg-secondary border border-border-secondary text-text-primary p-4 focus:border-text-muted outline-none transition-colors resize-none"
                  placeholder="Tell us anything you'd like us to know about your experience, certifications or availability. Optional."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FileUpload
                  label="CV Upload"
                  required
                  onFileSelect={setCvFile}
                  file={cvFile}
                />
                <FileUpload
                  label="Additional Documents (Optional)"
                  onFileSelect={setDocFile}
                  file={docFile}
                />
              </div>

              <p className="body-sm text-text-muted">
                Examples: certifications, trade tests, Red Seal, licences, visa, passport.
              </p>

              <div className="border border-border-secondary bg-bg-secondary p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-text-muted shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-xs text-text-secondary leading-relaxed">
                    Your information is handled securely and reviewed only by our deployment team. We never share applicant information with third parties without your permission.
                  </p>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full btn-primary py-4 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'SUBMITTING...' : 'JOIN THE STRATON NETWORK'}
              </button>

              <p className="body-sm text-center text-text-muted">
                All applications are confidential. Data is handled in accordance with our{" "}
                <a href="/privacy-policy" className="relative z-20 underline hover:text-text-primary transition-colors">
                  Privacy Policy
                </a>{" "}
                and is used solely for deployment assessment purposes.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: 'Industrial Automation Specialist — STRATON Group',
          description:
            'Join an international network of industrial automation specialists. Opportunities for PLC programmers, robot programmers, electrical and mechanical specialists across North America.',
          hiringOrganization: {
            '@type': 'Organization',
            name: 'STRATON Group',
            sameAs: 'https://www.stratongroup.co.za',
          },
          employmentType: 'CONTRACTOR',
          jobLocationType: 'TELECOMMUTE',
          applicantLocationRequirements: {
            '@type': 'Country',
            name: 'South Africa',
          },
        })}
      </script>
    </main>
  );
}
